"use client";

import { IconChevronsUpDownSm } from "@teral/icons";
import { Skeleton } from "@teral/ui";
import { cn } from "cn";
import type { Route } from "next";
import Link from "next/link";
import { type ReactNode, createContext, useContext } from "react";
import { CrumbPopover, type CrumbPopoverFooter, type CrumbPopoverItem } from "./crumb-popover";

/**
 * Crumbs render inline in the mobile top bar and stacked in the desktop
 * sidebar, where they read as a path: workspace, then project, then app.
 */
export type CrumbVariant = "inline" | "rail";

export const CrumbVariantContext = createContext<CrumbVariant>("inline");

type CrumbProps = {
  icon: ReactNode;
  label: string;
  href: string;
  items: CrumbPopoverItem[];
  currentId: string;
  searchPlaceholder: string;
  emptyText: string;
  footer: CrumbPopoverFooter;
  loading?: boolean;
  listStatus?: ReactNode;
};

export function Crumb({
  icon,
  label,
  href,
  items,
  currentId,
  searchPlaceholder,
  emptyText,
  footer,
  loading = false,
  listStatus,
}: CrumbProps) {
  const variant = useContext(CrumbVariantContext);
  const rail = variant === "rail";
  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-0.5",
        rail &&
          "group/crumb w-full rounded-md transition-colors hover:bg-grayA-3 has-data-[popup-open]:bg-grayA-3",
      )}
    >
      <CrumbLink icon={icon} label={label} href={href} loading={loading} />
      <CrumbPopover
        items={items}
        currentId={currentId}
        searchPlaceholder={searchPlaceholder}
        emptyText={emptyText}
        footer={footer}
        listStatus={listStatus}
        side={rail ? "right" : "bottom"}
      >
        <button
          type="button"
          className={cn(
            "hidden size-6 shrink-0 items-center justify-center rounded-md text-gray-9 transition-colors hover:bg-grayA-3 hover:text-gray-12 data-[popup-open]:bg-grayA-4 data-[popup-open]:text-gray-12 md:flex",
            rail &&
              "mr-1 ml-auto opacity-60 group-hover/crumb:opacity-100 data-[popup-open]:opacity-100 group-data-[collapsible=icon]:hidden",
          )}
          aria-label={`Switch ${label}`}
        >
          <IconChevronsUpDownSm />
        </button>
      </CrumbPopover>
    </div>
  );
}

export function CrumbLink({
  icon,
  label,
  href,
  loading = false,
  current = false,
}: {
  icon: ReactNode;
  label: string;
  href: string;
  loading?: boolean;
  current?: boolean;
}) {
  const rail = useContext(CrumbVariantContext) === "rail";
  return (
    <Link
      href={href as Route}
      aria-label={label}
      aria-current={current ? "page" : undefined}
      className={cn(
        "flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1 text-sm font-medium text-gray-12 transition-colors",
        rail
          ? "h-8 flex-1 px-2 outline-hidden focus-visible:outline-2 focus-visible:outline-(--focus-ring) group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 [&>span:last-child]:group-data-[collapsible=icon]:hidden"
          : "hover:bg-grayA-3",
        rail && current && "w-full rounded-md hover:bg-grayA-3",
      )}
    >
      {icon}
      {loading ? (
        <Skeleton className="h-3 w-20 bg-gray-4" />
      ) : (
        <span className={cn("truncate", !rail && "max-w-[120px] md:max-w-[180px]")}>{label}</span>
      )}
    </Link>
  );
}

export function CrumbSeparator() {
  return (
    <span
      aria-hidden="true"
      className="mx-1 h-4 w-px shrink-0 rotate-[18deg] rounded-full bg-grayA-7"
    />
  );
}
